// Architectural primitives — pure SVG. These replace the hand-drawn
// doodles on the rio festival site with the visual vocabulary of an
// architect's drawing set: blueprint grids, topographic contours, a
// compass rose, a scale bar, plumb lines, a section cut. Inheriting
// `currentColor` lets each glyph re-tint per section without
// rebuilding the artwork.

export const BlueprintGrid = ({
  width = "100%",
  height = "100%",
  color = "currentColor",
  opacity = 0.18,
  step = 32,
  ...rest
}) => (
  <svg
    width={width}
    height={height}
    aria-hidden="true"
    style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
    {...rest}
  >
    <defs>
      <pattern id={`bp-grid-${step}`} width={step} height={step} patternUnits="userSpaceOnUse">
        <path
          d={`M ${step} 0 L 0 0 0 ${step}`}
          fill="none"
          stroke={color}
          strokeWidth="0.6"
          opacity={opacity}
        />
      </pattern>
      <pattern id={`bp-grid-major-${step}`} width={step * 5} height={step * 5} patternUnits="userSpaceOnUse">
        <rect width={step * 5} height={step * 5} fill={`url(#bp-grid-${step})`} />
        <path
          d={`M ${step * 5} 0 L 0 0 0 ${step * 5}`}
          fill="none"
          stroke={color}
          strokeWidth="0.9"
          opacity={opacity * 1.6}
        />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill={`url(#bp-grid-major-${step})`} />
  </svg>
);

// Stacked topographic contours — animated to draw themselves as the
// element scrolls into view. Strokes use stroke-dasharray + offset
// triggered by `.in-view`.
export const ContourLines = ({
  className = "",
  color = "currentColor",
  width = 600,
  height = 240,
  ...rest
}) => (
  <svg
    viewBox={`0 0 ${width} ${height}`}
    className={className}
    aria-hidden="true"
    fill="none"
    stroke={color}
    {...rest}
  >
    {[0, 1, 2, 3, 4, 5, 6].map((i) => {
      const y = 30 + i * 28;
      // Gentle waves that read as ridge lines on a topo map.
      const d = `M -20 ${y} C ${width * 0.18} ${y - 22 + i * 3}, ${width * 0.42} ${y + 18 - i * 2}, ${width * 0.6} ${y - 10}, ${width * 0.78} ${y + 14}, ${width + 20} ${y - 6}`;
      return (
        <path
          key={i}
          d={d}
          strokeWidth={i % 2 ? 1.1 : 0.7}
          opacity={1 - i * 0.08}
          strokeLinecap="round"
          style={{
            strokeDasharray: 1400,
            strokeDashoffset: 1400,
            animationDelay: `${i * 90}ms`,
          }}
        />
      );
    })}
  </svg>
);

// North-up compass rose. Decorative; pairs nicely with section labels.
export const CompassRose = ({ size = 80, color = "currentColor", ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true" {...rest}>
    <circle cx="50" cy="50" r="44" fill="none" stroke={color} strokeWidth="0.8" opacity="0.5" />
    <circle cx="50" cy="50" r="32" fill="none" stroke={color} strokeWidth="0.6" opacity="0.35" />
    {/* Cardinal points */}
    <path d="M50 6 L54 50 L50 50 Z" fill={color} />
    <path d="M50 6 L46 50 L50 50 Z" fill={color} opacity="0.45" />
    <path d="M50 94 L54 50 L50 50 Z" fill={color} opacity="0.35" />
    <path d="M50 94 L46 50 L50 50 Z" fill={color} opacity="0.55" />
    <path d="M6 50 L50 46 L50 50 Z" fill={color} opacity="0.45" />
    <path d="M6 50 L50 54 L50 50 Z" fill={color} opacity="0.35" />
    <path d="M94 50 L50 46 L50 50 Z" fill={color} opacity="0.35" />
    <path d="M94 50 L50 54 L50 50 Z" fill={color} opacity="0.45" />
    <text
      x="50"
      y="14"
      fontSize="9"
      textAnchor="middle"
      fill={color}
      fontFamily="var(--font-mono)"
      fontWeight="600"
    >
      N
    </text>
    <circle cx="50" cy="50" r="2.4" fill={color} />
  </svg>
);

export const ScaleBar = ({
  width = 220,
  color = "currentColor",
  label = "SCALE 1:48",
  ...rest
}) => (
  <svg width={width} height={28} viewBox={`0 0 ${width} 28`} aria-hidden="true" {...rest}>
    {/* Alternating filled/empty cells */}
    {Array.from({ length: 8 }).map((_, i) => {
      const w = (width - 40) / 8;
      const x = 20 + i * w;
      return (
        <rect
          key={i}
          x={x}
          y={6}
          width={w}
          height={6}
          fill={i % 2 ? color : "none"}
          stroke={color}
          strokeWidth="0.8"
        />
      );
    })}
    <text
      x={20}
      y={24}
      fontSize="8"
      fill={color}
      fontFamily="var(--font-mono)"
      letterSpacing="2"
    >
      {label}
    </text>
  </svg>
);

// Section cut indicator (A-A, B-B, etc) — paired with a dashed cut line.
export const SectionCut = ({
  width = "100%",
  height = 44,
  color = "currentColor",
  label = "A-A",
  ...rest
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 600 44"
    preserveAspectRatio="none"
    aria-hidden="true"
    {...rest}
  >
    <line
      x1="60"
      y1="22"
      x2="540"
      y2="22"
      stroke={color}
      strokeWidth="1"
      strokeDasharray="14 6 2 6"
    />
    {/* End markers (filled triangle pointing inward) */}
    <g transform="translate(20 22)">
      <circle r="14" fill="none" stroke={color} strokeWidth="1.2" />
      <path d="M 4 -7 L 14 0 L 4 7 Z" fill={color} />
      <text
        x="0"
        y="3"
        fontSize="9"
        textAnchor="middle"
        fill={color}
        fontFamily="var(--font-mono)"
        fontWeight="600"
      >
        {label}
      </text>
    </g>
    <g transform="translate(580 22) scale(-1 1)">
      <circle r="14" fill="none" stroke={color} strokeWidth="1.2" />
      <path d="M 4 -7 L 14 0 L 4 7 Z" fill={color} />
      <text
        x="0"
        y="3"
        fontSize="9"
        textAnchor="middle"
        fill={color}
        fontFamily="var(--font-mono)"
        fontWeight="600"
        transform="scale(-1 1)"
      >
        {label}
      </text>
    </g>
  </svg>
);

// Animated dimension line — used as a divider between sections.
export const DimensionLine = ({
  height = 30,
  color = "currentColor",
  label,
  ...rest
}) => (
  <svg
    width="100%"
    height={height}
    viewBox={`0 0 600 ${height}`}
    preserveAspectRatio="none"
    aria-hidden="true"
    {...rest}
  >
    <line x1="0" y1={height / 2} x2="600" y2={height / 2} stroke={color} strokeWidth="0.8" opacity="0.6" />
    {/* Tick marks */}
    {Array.from({ length: 13 }).map((_, i) => (
      <line
        key={i}
        x1={i * 50}
        y1={height / 2 - 5}
        x2={i * 50}
        y2={height / 2 + 5}
        stroke={color}
        strokeWidth="0.8"
        opacity="0.5"
      />
    ))}
    {/* Arrow heads */}
    <path
      d={`M 6 ${height / 2} L 16 ${height / 2 - 4} L 16 ${height / 2 + 4} Z`}
      fill={color}
      opacity="0.7"
    />
    <path
      d={`M 594 ${height / 2} L 584 ${height / 2 - 4} L 584 ${height / 2 + 4} Z`}
      fill={color}
      opacity="0.7"
    />
    {label && (
      <text
        x="300"
        y={height / 2 - 8}
        fontSize="9"
        textAnchor="middle"
        fill={color}
        fontFamily="var(--font-mono)"
        letterSpacing="2"
        opacity="0.85"
      >
        {label}
      </text>
    )}
  </svg>
);

// Plumb line + level — animated swing in CSS.
export const PlumbBob = ({ size = 60, color = "currentColor", ...rest }) => (
  <svg width={size} height={size * 1.4} viewBox="0 0 60 84" aria-hidden="true" {...rest}>
    <line x1="30" y1="0" x2="30" y2="58" stroke={color} strokeWidth="0.8" opacity="0.7" />
    <path
      d="M30 58 L24 70 L36 70 Z M30 58 L20 70 L40 70 Z"
      fill="none"
      stroke={color}
      strokeWidth="1"
    />
    <path d="M22 70 L30 82 L38 70 Z" fill={color} />
  </svg>
);

// Drafted axonometric house — used as a hero glyph that animates in.
export const AxoHouse = ({
  size = 320,
  color = "currentColor",
  accent = "currentColor",
  className = "",
  ...rest
}) => (
  <svg
    width={size}
    height={size * 0.85}
    viewBox="0 0 400 340"
    aria-hidden="true"
    className={className}
    fill="none"
    stroke={color}
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...rest}
  >
    {/* Ground line */}
    <line x1="20" y1="300" x2="380" y2="300" strokeDasharray="3 4" opacity="0.5" />
    {/* Lower volume (simple rectangular prism in axonometric) */}
    <g className="axo-lower">
      <path d="M80 240 L80 300 L240 300 L240 240 Z" />
      <path d="M80 240 L120 220 L280 220 L240 240 Z" />
      <path d="M240 240 L240 300 L280 280 L280 220 Z" />
      {/* Door */}
      <rect x="150" y="262" width="22" height="38" />
      {/* Windows */}
      <rect x="100" y="252" width="30" height="24" />
      <rect x="200" y="252" width="22" height="24" />
    </g>
    {/* Upper volume — the cantilever above */}
    <g className="axo-upper">
      <path d="M60 200 L60 240 L260 240 L260 200 Z" />
      <path d="M60 200 L100 180 L300 180 L260 200 Z" />
      <path d="M260 200 L260 240 L300 220 L300 180 Z" />
      {/* Long ribbon window */}
      <line x1="80" y1="220" x2="240" y2="220" strokeWidth="1" />
      <line x1="80" y1="226" x2="240" y2="226" strokeWidth="1" opacity="0.5" />
    </g>
    {/* Stair */}
    <g className="axo-stair" stroke={accent}>
      <path d="M260 240 L300 220 L320 230 L280 250 Z" />
      <path d="M268 244 L304 226" />
      <path d="M276 248 L308 232" />
      <path d="M284 252 L312 238" />
    </g>
    {/* Roof line accent */}
    <line x1="60" y1="200" x2="100" y2="180" stroke={accent} strokeWidth="2" />
    <line x1="100" y1="180" x2="300" y2="180" stroke={accent} strokeWidth="2" />
    <line x1="300" y1="180" x2="260" y2="200" stroke={accent} strokeWidth="2" />
    {/* Dimension callout */}
    <g opacity="0.6">
      <line x1="80" y1="320" x2="240" y2="320" />
      <line x1="80" y1="316" x2="80" y2="324" />
      <line x1="240" y1="316" x2="240" y2="324" />
      <text
        x="160"
        y="334"
        fontSize="10"
        textAnchor="middle"
        fill={color}
        stroke="none"
        fontFamily="var(--font-mono)"
        letterSpacing="1.5"
      >
        24'-0"
      </text>
    </g>
  </svg>
);
